// NOVA STRIKE — synth music engine
// 120 BPM 4/4, 16-step bar, lookahead scheduler for sample-accurate timing.
// 5 voices: drums (kick/snare/hat), bass, pad, arp, lead — each with its own gain bus.
// Three sections (title / play / boss) crossfade voice gains; pattern keeps looping.
// D harmonic minor, classic synthwave progression: i — VI — III — VII = Dm — Bb — F — C.

import { settings } from './settings';

// Perceptual (square-law) mapping: linear slider 0-1 → gain 0-1
// At 50% slider position → 25% gain (~-12 dB), feels natural for ears.
const logGain = (v: number): number => v * v;

type Section = 'title' | 'play' | 'boss' | 'outro';

let AC: AudioContext | null = null;
let MASTER: GainNode | null = null;
let REVERB: ConvolverNode | null = null;
let REVERB_WET: GainNode | null = null;

// Per-voice gain buses (target gains lerp toward section preset)
let gKick: GainNode | null = null;
let gSnare: GainNode | null = null;
let gHat: GainNode | null = null;
let gBass: GainNode | null = null;
let gPad: GainNode | null = null;
let gArp: GainNode | null = null;
let gLead: GainNode | null = null;
let duckBus: GainNode | null = null; // side-chain target; pad + arp pass through here

const SECTION_GAINS: Record<Section, { kick: number; snare: number; hat: number; bass: number; pad: number; arp: number; lead: number }> = {
  title: { kick: 0.00, snare: 0.00, hat: 0.00, bass: 0.00, pad: 0.55, arp: 0.34, lead: 0.18 },
  play:  { kick: 0.55, snare: 0.40, hat: 0.24, bass: 0.50, pad: 0.30, arp: 0.36, lead: 0.30 },
  boss:  { kick: 0.70, snare: 0.55, hat: 0.34, bass: 0.60, pad: 0.30, arp: 0.42, lead: 0.55 },
  outro: { kick: 0.00, snare: 0.00, hat: 0.00, bass: 0.00, pad: 0.30, arp: 0.00, lead: 0.00 },
};

let curSection: Section = 'title';
let targetSection: Section = 'title';

// Scheduler state
const BPM = 124;
const STEP_SEC = 60 / BPM / 4; // 16th-note duration
const BAR_STEPS = 16;
const LOOP_BARS = 4; // 4-bar loop = 64 steps
let nextStepTime = 0; // AC.currentTime when the next step lands
let stepIdx = 0; // 0..63 within the 4-bar loop (kept for fill-bar / pad / lead phrasing)
let songStep = 0; // 0..(SONG_BARS*16-1) — full 64-bar form, never short-loops
let loopCount = 0; // counts each 4-bar wrap; fill bar fires on odd loopCount last bar
let schedTimer: ReturnType<typeof setInterval> | null = null;
let lastSectionForRiser: Section = 'title';
let riserPendingAt = -1; // stepIdx where to schedule riser (next bar boundary), -1 = none

let bgmOn = localStorage.getItem('ns_music_pixi') !== '0';

// D harmonic minor scale (MIDI):  D=62, E=64, F=65, G=67, A=69, Bb=70, C#=73, D=74
function midi(n: number): number { return 440 * Math.pow(2, (n - 69) / 12); }

// Four chord-progression banks. Each bank has 4 chords held one bar each.
const PROG_ROOTS: number[][] = [
  [62, 58, 65, 60], // 0: Dm-Bb-F-C  (synthwave classic)
  [57, 65, 60, 67], // 1: Am-F-C-G   (uplifting)
  [55, 63, 58, 65], // 2: Gm-Eb-Bb-F (darker)
  [64, 60, 67, 62], // 3: Em-C-G-D   (key-up)
];
const PROG_TRIADS: number[][][] = [
  [[62,65,69], [58,62,65], [65,69,72], [60,64,67]],
  [[57,60,64], [65,69,72], [60,64,67], [67,71,74]],
  [[55,58,62], [63,67,70], [58,62,65], [65,69,72]],
  [[64,67,71], [60,64,67], [67,71,74], [62,65,69]],
];

// Song form — 64 bars (~124s @ 124 BPM) before any chord-level repeat. 8 sections × 8 bars.
//   0-7: intro (Dm)         — pad+arp only
//   8-15: verse A (Dm)       — drums + bass enter
//  16-23: pre-chorus (Am)    — lead joins
//  24-31: chorus (Am)        — full stack
//  32-39: breakdown (Gm)     — drums drop, lead steady
//  40-47: verse B (Dm)       — drums return
//  48-55: bridge (Em)        — key shift
//  56-63: outro chorus (Am)  — full peak
const SONG_BARS = 64;
const SONG_PROG_PER_BAR: number[] = [
  0,0,0,0, 0,0,0,0,   // intro
  0,0,0,0, 0,0,0,0,   // verse A
  1,1,1,1, 1,1,1,1,   // pre-chorus
  1,1,1,1, 1,1,1,1,   // chorus
  2,2,2,2, 2,2,2,2,   // breakdown
  0,0,0,0, 0,0,0,0,   // verse B
  3,3,3,3, 3,3,3,3,   // bridge
  1,1,1,1, 1,1,1,1,   // outro chorus
];
// Per-section dynamics 0..1 (sections 0..7)
const SECTION_DRUMS = [0.0, 0.7, 0.9, 1.0, 0.0, 0.85, 0.8, 1.0];
const SECTION_LEAD  = [0.0, 0.0, 0.6, 1.0, 0.6, 0.4, 0.85, 1.0];
const SECTION_BASS  = [0.0, 1.0, 1.0, 1.0, 0.6, 1.0, 1.0, 1.0];

// Lead melody — 4-bar phrase, 16 steps per bar. -1 = rest.
// Crafted to peak in bar 3 (over F major) for emotional lift.
const LEAD_NOTES: number[] = [
  // Bar 1 (Dm)
  74, -1, 77, -1, 74, -1, 70, -1, 69, -1, -1, -1, 70, -1, 74, -1,
  // Bar 2 (Bb)
  77, -1, 74, -1, 70, -1, 69, -1, 65, -1, -1, -1, 69, -1, 70, -1,
  // Bar 3 (F) — peak
  72, -1, 76, -1, 81, -1, 79, -1, 76, -1, 74, -1, 72, -1, 69, -1,
  // Bar 4 (C) — descend back home
  72, -1, 70, -1, 69, -1, 67, -1, 65, -1, 62, -1, -1, -1, -1, -1,
];

function ensure(): AudioContext {
  if (!AC) {
    AC = new (window.AudioContext || (window as any).webkitAudioContext)();
    MASTER = AC.createGain();
    MASTER.gain.value = settings.master;
    const comp = AC.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.ratio.value = 6;
    comp.attack.value = 0.005;
    comp.release.value = 0.12;
    comp.knee.value = 6;
    MASTER.connect(comp).connect(AC.destination);

    // Reverb impulse — exponential-decay filtered noise, ~1.4s tail
    REVERB = AC.createConvolver();
    const tail = 1.4;
    const len = Math.floor(AC.sampleRate * tail);
    const ir = AC.createBuffer(2, len, AC.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        const t = i / len;
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 2.5);
      }
    }
    REVERB.buffer = ir;
    REVERB_WET = AC.createGain();
    REVERB_WET.gain.value = 0.30;
    REVERB.connect(REVERB_WET).connect(MASTER);

    // Side-chain duck bus — pad + arp pass through, dipped on each kick
    duckBus = AC.createGain();
    duckBus.gain.value = 1;
    duckBus.connect(MASTER);

    // Per-voice buses
    gKick  = AC.createGain(); gKick.gain.value  = 0; gKick.connect(MASTER);
    gSnare = AC.createGain(); gSnare.gain.value = 0; gSnare.connect(MASTER);
    gHat   = AC.createGain(); gHat.gain.value   = 0; gHat.connect(MASTER);
    gBass  = AC.createGain(); gBass.gain.value  = 0; gBass.connect(MASTER);
    gPad   = AC.createGain(); gPad.gain.value   = 0; gPad.connect(duckBus);
    gArp   = AC.createGain(); gArp.gain.value   = 0;
    // Arp gets a short reverb send (pre-duck so the reverb tail isn't pumped)
    gArp.connect(duckBus);
    gArp.connect(REVERB);
    gLead  = AC.createGain(); gLead.gain.value  = 0;
    gLead.connect(MASTER);
    gLead.connect(REVERB);
  }
  if (AC.state === 'suspended') AC.resume();
  return AC;
}

export function applyVolumes(): void {
  if (MASTER) MASTER.gain.value = logGain(settings.master);
}

export function unlockAudio(): void { AC?.resume(); }

export function setSuspended(suspended: boolean): void {
  if (!AC) return;
  if (suspended) {
    // Belt-and-suspenders: zero master gain INSTANTLY (avoids lerped engine drone
    // sticking when rAF throttles), cancel any pending ramps, then suspend the
    // AudioContext so scheduled timeline values stop advancing.
    if (MASTER) {
      MASTER.gain.cancelScheduledValues(AC.currentTime);
      MASTER.gain.setValueAtTime(0, AC.currentTime);
      MASTER.gain.value = 0;
    }
    if (AC.state === 'running') AC.suspend();
  } else {
    if (AC.state === 'suspended') AC.resume();
    if (MASTER) {
      MASTER.gain.cancelScheduledValues(AC.currentTime);
      MASTER.gain.setValueAtTime(settings.master, AC.currentTime);
      MASTER.gain.value = settings.master;
    }
  }
}

// --- Voices ---

function noiseBuffer(ac: AudioContext, dur: number): AudioBuffer {
  const len = Math.floor(ac.sampleRate * dur);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

function duckOnKick(t: number): void {
  // Dip to 0.55 instantly at the kick, recover to 1.0 over 0.18s
  if (!duckBus) return;
  const g = duckBus.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(0.55, t);
  g.linearRampToValueAtTime(1.0, t + 0.18);
}

function playRiser(t: number, dur: number, vol = 0.22): void {
  const ac = AC!;
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, dur);
  const f = ac.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.setValueAtTime(400, t);
  f.frequency.exponentialRampToValueAtTime(6000, t + dur);
  f.Q.value = 6;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol * logGain(settings.music), t + dur * 0.85);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  n.connect(f).connect(g).connect(MASTER!);
  n.start(t); n.stop(t + dur + 0.02);
}

function playKick(t: number): void {
  const ac = AC!;
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(110, t);
  o.frequency.exponentialRampToValueAtTime(35, t + 0.18);
  g.gain.setValueAtTime(0.95 * logGain(settings.music), t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
  o.connect(g).connect(gKick!);
  o.start(t); o.stop(t + 0.34);
  // Click transient
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, 0.02);
  const ng = ac.createGain();
  const nf = ac.createBiquadFilter();
  nf.type = 'highpass'; nf.frequency.value = 800;
  ng.gain.setValueAtTime(0.4 * logGain(settings.music), t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 0.025);
  n.connect(nf).connect(ng).connect(gKick!);
  n.start(t); n.stop(t + 0.03);
}

function playSnare(t: number): void {
  const ac = AC!;
  // Noise body
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, 0.2);
  const nf = ac.createBiquadFilter();
  nf.type = 'highpass'; nf.frequency.value = 1500;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.55 * logGain(settings.music), t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
  n.connect(nf).connect(ng).connect(gSnare!);
  n.start(t); n.stop(t + 0.2);
  // Pitch body
  const o = ac.createOscillator();
  const og = ac.createGain();
  o.type = 'triangle';
  o.frequency.setValueAtTime(220, t);
  o.frequency.exponentialRampToValueAtTime(140, t + 0.10);
  og.gain.setValueAtTime(0.35 * logGain(settings.music), t);
  og.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
  o.connect(og).connect(gSnare!);
  o.start(t); o.stop(t + 0.14);
}

function playHat(t: number, accent = false): void {
  const ac = AC!;
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, 0.05);
  const nf = ac.createBiquadFilter();
  nf.type = 'highpass'; nf.frequency.value = 6000;
  const ng = ac.createGain();
  const vol = (accent ? 0.42 : 0.22) * logGain(settings.music);
  ng.gain.setValueAtTime(vol, t);
  ng.gain.exponentialRampToValueAtTime(0.001, t + (accent ? 0.06 : 0.035));
  n.connect(nf).connect(ng).connect(gHat!);
  n.start(t); n.stop(t + 0.06);
}

function playBass(t: number, midiNote: number, dur: number): void {
  const ac = AC!;
  const o = ac.createOscillator();
  const o2 = ac.createOscillator();
  const f = ac.createBiquadFilter();
  const g = ac.createGain();
  o.type = 'square';
  o2.type = 'sawtooth';
  // Bass octave below
  const freq = midi(midiNote - 12);
  o.frequency.value = freq;
  o2.frequency.value = freq * 1.005; // slight detune
  f.type = 'lowpass';
  f.frequency.setValueAtTime(180, t);
  f.frequency.exponentialRampToValueAtTime(900, t + 0.04);
  f.frequency.exponentialRampToValueAtTime(220, t + dur);
  f.Q.value = 6;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.55 * logGain(settings.music), t + 0.005);
  g.gain.linearRampToValueAtTime(0.45 * logGain(settings.music), t + 0.04);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(f); o2.connect(f);
  f.connect(g).connect(gBass!);
  o.start(t); o2.start(t);
  o.stop(t + dur + 0.02); o2.stop(t + dur + 0.02);
}

function playPadChord(t: number, notes: number[], dur: number): void {
  const ac = AC!;
  const f = ac.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.setValueAtTime(700, t);
  f.frequency.linearRampToValueAtTime(1400, t + dur * 0.5);
  f.frequency.linearRampToValueAtTime(700, t + dur);
  f.Q.value = 1;
  const g = ac.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.18 * logGain(settings.music), t + 0.4);
  g.gain.linearRampToValueAtTime(0.18 * logGain(settings.music), t + dur - 0.4);
  g.gain.linearRampToValueAtTime(0.001, t + dur);
  for (const n of notes) {
    const fr = midi(n);
    for (const detune of [-7, +7]) { // 2 detuned saws per note
      const o = ac.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = fr;
      o.detune.value = detune;
      o.connect(f);
      o.start(t); o.stop(t + dur + 0.05);
    }
  }
  f.connect(g).connect(gPad!);
}

function playArp(t: number, midiNote: number): void {
  const ac = AC!;
  const o = ac.createOscillator();
  const f = ac.createBiquadFilter();
  const g = ac.createGain();
  o.type = 'square';
  o.frequency.value = midi(midiNote);
  f.type = 'bandpass';
  f.frequency.value = 1500;
  f.Q.value = 4;
  const dur = 0.16;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.16 * logGain(settings.music), t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(f).connect(g).connect(gArp!);
  o.start(t); o.stop(t + dur + 0.01);
}

function playLead(t: number, midiNote: number, dur: number): void {
  const ac = AC!;
  const o = ac.createOscillator();
  const o2 = ac.createOscillator();
  const lfo = ac.createOscillator();
  const lfoG = ac.createGain();
  const f = ac.createBiquadFilter();
  const g = ac.createGain();
  o.type = 'sawtooth';
  o2.type = 'square';
  const fr = midi(midiNote);
  o.frequency.value = fr;
  o2.frequency.value = fr;
  o2.detune.value = 6;
  // Vibrato 5Hz, ±8 cents
  lfo.frequency.value = 5;
  lfoG.gain.value = 8;
  lfo.connect(lfoG);
  lfoG.connect(o.detune as any);
  lfoG.connect(o2.detune as any);
  f.type = 'lowpass';
  f.frequency.value = 3500;
  f.Q.value = 2;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.32 * logGain(settings.music), t + 0.02);
  g.gain.linearRampToValueAtTime(0.22 * logGain(settings.music), t + dur * 0.6);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(f); o2.connect(f);
  f.connect(g).connect(gLead!);
  o.start(t); o2.start(t); lfo.start(t);
  o.stop(t + dur + 0.02); o2.stop(t + dur + 0.02); lfo.stop(t + dur + 0.02);
}

// --- Scheduler ---

function scheduleStep(stepIdx: number, t: number): void {
  const stepInBar = stepIdx % BAR_STEPS;
  const barIdx = Math.floor(stepIdx / BAR_STEPS) % LOOP_BARS; // 0..3 within 4-bar phrase block
  // Song-form chord pick: 64 bars before any chord-level repeat
  const songBar = Math.floor(songStep / BAR_STEPS) % SONG_BARS;
  const progIdx = SONG_PROG_PER_BAR[songBar];
  const chordIdx = songBar % 4;
  const triad = PROG_TRIADS[progIdx][chordIdx];
  const root = PROG_ROOTS[progIdx][chordIdx];
  const sectionIdx = Math.min(7, Math.floor(songBar / 8));
  const drumScale = SECTION_DRUMS[sectionIdx];
  const leadScale = SECTION_LEAD[sectionIdx];
  const bassScale = SECTION_BASS[sectionIdx];

  // Loop wrap detection — at start of each new 4-bar loop, advance loopCount
  if (stepIdx === 0) {
    loopCount += 1;
    // Schedule a pending riser at this downbeat if a section change waited for the boundary
    if (riserPendingAt >= 0) {
      playRiser(t, STEP_SEC * BAR_STEPS, 0.22);
      riserPendingAt = -1;
    }
  }

  // Fill bar = last bar of every 8-bar block (loopCount odd & barIdx === 3)
  const isFillBar = (loopCount % 2 === 1) && barIdx === 3;

  // Drums — section dynamics gate them; intro + breakdown sections drop drums entirely
  if (drumScale > 0.05) {
    if (isFillBar && stepInBar >= 12) {
      // Snare roll on the last 4 steps of the fill bar
      playSnare(t);
      if (stepInBar === 15) playKick(t);
    } else {
      if (stepInBar === 0 || stepInBar === 8) { playKick(t); duckOnKick(t); }
      if (stepInBar === 4 || stepInBar === 12) playSnare(t);
      // Hats on every 16th — quiet ghost on even, accent on odd (offbeat)
      playHat(t, stepInBar % 2 === 1);
    }
  }

  // Bass — root on 0 + 6 + 8 + 14, fifth on 10 (walking pattern). Section-gated.
  if (bassScale > 0.05) {
    if (stepInBar === 0)  playBass(t, root,     STEP_SEC * 5.5);
    if (stepInBar === 6)  playBass(t, root,     STEP_SEC * 1.5);
    if (stepInBar === 8)  playBass(t, root,     STEP_SEC * 1.5);
    if (stepInBar === 10) playBass(t, triad[2], STEP_SEC * 1.5);
    if (stepInBar === 14) playBass(t, root + 7, STEP_SEC * 1.5);
  }

  // Pad — full triad held for 1 bar, retrigger every bar
  if (stepInBar === 0) playPadChord(t, triad, STEP_SEC * BAR_STEPS);

  // Arp — pattern varies per bar so the loop never feels static.
  // Bar 0: 1-3-5-3   Bar 1: 1-5-3-1   Bar 2: 5-3-1-(oct)   Bar 3: 1-3-5-(oct)
  const arpStepIdx = stepInBar % 4;
  let arpDeg: number;
  if      (barIdx === 0) arpDeg = [0, 1, 2, 1][arpStepIdx];
  else if (barIdx === 1) arpDeg = [0, 2, 1, 0][arpStepIdx];
  else if (barIdx === 2) arpDeg = [2, 1, 0, 3][arpStepIdx]; // 3 = octave above root
  else                   arpDeg = [0, 1, 2, 3][arpStepIdx];
  const arpNote = arpDeg === 3 ? triad[0] + 12 : triad[arpDeg];
  // Lift bar 2's arp another octave for the drop
  const arpLift = barIdx === 2 ? 12 : 0;
  playArp(t, arpNote + 12 + arpLift);

  // Lead — 4-bar melody, one note per 16th step (or rest)
  // Lead — gated per-section. Transpose to fit the current chord progression's key.
  if (leadScale > 0.1) {
    const note = LEAD_NOTES[stepIdx % LEAD_NOTES.length];
    if (note > 0) {
      // Key-shift table per progression: Dm 0, Am -5, Gm +5, Em +2
      const keyShift = progIdx === 0 ? 0 : progIdx === 1 ? -5 : progIdx === 2 ? 5 : 2;
      playLead(t, note + keyShift, STEP_SEC * 1.8);
    }
  }
}

function lerpVoiceGains(): void {
  if (!AC) return;
  const target = SECTION_GAINS[targetSection];
  const k = 0.06; // crossfade speed
  const buses: [GainNode | null, number][] = [
    [gKick,  target.kick],
    [gSnare, target.snare],
    [gHat,   target.hat],
    [gBass,  target.bass],
    [gPad,   target.pad],
    [gArp,   target.arp],
    [gLead,  target.lead],
  ];
  for (const [bus, tg] of buses) {
    if (!bus) continue;
    const cur = bus.gain.value;
    const next = cur + (tg - cur) * k;
    bus.gain.setValueAtTime(next, AC.currentTime);
  }
  if (targetSection !== curSection) curSection = targetSection;
}

function schedulerTick(): void {
  if (!AC || !bgmOn) return;
  const ac = AC;
  lerpVoiceGains();
  const lookahead = 0.12; // seconds ahead to schedule
  while (nextStepTime < ac.currentTime + lookahead) {
    scheduleStep(stepIdx, nextStepTime);
    nextStepTime += STEP_SEC;
    stepIdx = (stepIdx + 1) % (BAR_STEPS * LOOP_BARS);
    songStep = (songStep + 1) % (BAR_STEPS * SONG_BARS);
  }
}

function startScheduler(): void {
  if (schedTimer) return;
  ensure();
  nextStepTime = AC!.currentTime + 0.05;
  stepIdx = 0;
  songStep = 0;
  schedTimer = setInterval(schedulerTick, 25);
}

function stopScheduler(): void {
  if (schedTimer) {
    clearInterval(schedTimer);
    schedTimer = null;
  }
  // Mute all voices immediately
  if (AC) {
    for (const bus of [gKick, gSnare, gHat, gBass, gPad, gArp, gLead]) {
      if (bus) bus.gain.setValueAtTime(0, AC.currentTime);
    }
  }
}

export function setMusicSection(s: Section): void {
  if (s !== targetSection && s !== lastSectionForRiser) {
    // Schedule a 1-bar riser sweep on the next downbeat to herald the change
    riserPendingAt = 0;
    lastSectionForRiser = s;
  }
  targetSection = s;
}

export function tickBGM(_dt: number, wave: number, isBoss: boolean, paused: boolean): void {
  if (!bgmOn || paused) {
    stopScheduler();
    return;
  }
  startScheduler();
  // Derive section: wave 0 = title, boss waves = boss, otherwise play
  const section: Section = wave <= 0 ? 'title' : isBoss ? 'boss' : 'play';
  setMusicSection(section);
}

export function toggleMusic(): boolean {
  bgmOn = !bgmOn;
  localStorage.setItem('ns_music_pixi', bgmOn ? '1' : '0');
  if (!bgmOn) stopScheduler();
  return bgmOn;
}
export function isMusicOn(): boolean { return bgmOn; }

// --- Continuous engine + friction layer ---
// Two voices that run forever once started:
//   * Engine roar — saw + sub-triangle stacked, lowpass filter modulated by throttle
//   * Friction wind — looping noise through a bandpass, gain proportional to speed
// `setEngineState(speed01, boost, alive)` is called every frame and lerps params.

let eng_root: OscillatorNode | null = null;
let eng_sub: OscillatorNode | null = null;
let eng_filter: BiquadFilterNode | null = null;
let eng_gain: GainNode | null = null;
let frc_src: AudioBufferSourceNode | null = null;
let frc_filter: BiquadFilterNode | null = null;
let frc_gain: GainNode | null = null;
let engine_started = false;
let eng_target_freq = 80;
let eng_target_gain = 0;
let eng_target_filter = 400;
let frc_target_gain = 0;
let frc_target_freq = 800;
// Manual-tracked current values — Web Audio's gain.value reads can be stale once we schedule
let eng_cur_freq = 80;
let eng_cur_gain = 0;
let eng_cur_filter = 400;
let frc_cur_gain = 0;
let frc_cur_freq = 800;

function startEngine(): void {
  if (engine_started) return;
  const ac = ensure();
  // Engine hum — smooth triangle + sine sub for a sci-fi warp-drive feel
  eng_filter = ac.createBiquadFilter();
  eng_filter.type = 'lowpass';
  eng_filter.frequency.value = 400;
  eng_filter.Q.value = 1.5;
  eng_gain = ac.createGain();
  eng_gain.gain.value = 0;
  eng_root = ac.createOscillator();
  eng_root.type = 'triangle';
  eng_root.frequency.value = 80;
  eng_sub = ac.createOscillator();
  eng_sub.type = 'sine';
  eng_sub.frequency.value = 40;
  eng_root.connect(eng_filter);
  eng_sub.connect(eng_filter);
  eng_filter.connect(eng_gain).connect(MASTER!);
  eng_root.start();
  eng_sub.start();
  // Friction wind — looping noise through bandpass
  const noiseLen = ac.sampleRate * 2;
  const buf = ac.createBuffer(1, noiseLen, ac.sampleRate);
  const d = buf.getChannelData(0);
  // Pink-ish noise via running average
  let lastOut = 0;
  for (let i = 0; i < noiseLen; i++) {
    const w = Math.random() * 2 - 1;
    lastOut = (lastOut * 0.92 + w * 0.08);
    d[i] = lastOut * 3.5;
  }
  frc_src = ac.createBufferSource();
  frc_src.buffer = buf;
  frc_src.loop = true;
  frc_filter = ac.createBiquadFilter();
  frc_filter.type = 'bandpass';
  frc_filter.frequency.value = 800;
  frc_filter.Q.value = 1.4;
  frc_gain = ac.createGain();
  frc_gain.gain.value = 0;
  frc_src.connect(frc_filter).connect(frc_gain).connect(MASTER!);
  frc_src.start();
  engine_started = true;
}

// Called every frame from main.ts. speed01 in [0..1]; alive=false silences both.
export function setEngineState(speed01: number, boost: boolean, alive: boolean): void {
  if (!alive) {
    eng_target_gain = 0;
    frc_target_gain = 0;
    if (engine_started) tickEngine();
    return;
  }
  startEngine();
  const s = Math.max(0, Math.min(1, speed01));
  // Engine tone — essentially OFF. The "ship sound" is now just air friction.
  eng_target_freq = 60;
  eng_target_filter = 200;
  eng_target_gain = 0;
  // Friction wind — only audible sound. Comes in as the ship moves, fades at rest.
  const fric = s < 0.05 ? 0 : Math.pow((s - 0.05) / 0.95, 1.2);
  frc_target_gain = fric * 0.18 * logGain(settings.sfx);
  frc_target_freq = 800 + s * 1400 + (boost ? 400 : 0);
  tickEngine();
}

function tickEngine(): void {
  if (!engine_started || !AC) return;
  const k = 0.12;
  const t = AC.currentTime;
  eng_cur_gain += (eng_target_gain - eng_cur_gain) * k;
  eng_gain!.gain.setValueAtTime(eng_cur_gain, t);
  eng_cur_freq += (eng_target_freq - eng_cur_freq) * k;
  eng_root!.frequency.setValueAtTime(eng_cur_freq, t);
  eng_sub!.frequency.setValueAtTime(eng_cur_freq * 0.5, t);
  eng_cur_filter += (eng_target_filter - eng_cur_filter) * k;
  eng_filter!.frequency.setValueAtTime(eng_cur_filter, t);
  frc_cur_gain += (frc_target_gain - frc_cur_gain) * k;
  frc_gain!.gain.setValueAtTime(frc_cur_gain, t);
  frc_cur_freq += (frc_target_freq - frc_cur_freq) * k;
  frc_filter!.frequency.setValueAtTime(frc_cur_freq, t);
}

// --- One-shot SFX (kept compatible with previous API) ---

function tone(freq: number, dur: number, type: OscillatorType = 'square', vol = 0.3, slide = 0): void {
  const ac = ensure();
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  o.frequency.value = freq;
  if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(20, freq + slide), ac.currentTime + dur);
  g.gain.value = vol * logGain(settings.sfx);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  o.connect(g).connect(MASTER!);
  o.start();
  o.stop(ac.currentTime + dur);
}

const last: Record<string, number> = {};
function gate(key: string, ms: number): boolean {
  const now = performance.now();
  if (last[key] && now - last[key] < ms) return false;
  last[key] = now;
  return true;
}

// --- Per-weapon shot voices ---

function noiseShot(centerHz: number, q: number, dur: number, vol: number): void {
  const ac = ensure();
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, dur);
  const f = ac.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = centerHz;
  f.Q.value = q;
  const g = ac.createGain();
  g.gain.setValueAtTime(vol * logGain(settings.sfx), ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  n.connect(f).connect(g).connect(MASTER!);
  n.start();
  n.stop(ac.currentTime + dur + 0.01);
}

function chord(freqs: number[], dur: number, type: OscillatorType, vol: number): void {
  const ac = ensure();
  const g = ac.createGain();
  g.gain.setValueAtTime(vol * logGain(settings.sfx), ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
  for (const f of freqs) {
    const o = ac.createOscillator();
    o.type = type;
    o.frequency.value = f;
    o.connect(g);
    o.start();
    o.stop(ac.currentTime + dur + 0.02);
  }
  g.connect(MASTER!);
}

// HQ shot helper: stacked osc with optional reverb send + filter + envelope.
// Each oscillator: { type, freq, detune?, vol, slide? }
type Osc = { type: OscillatorType; freq: number; detune?: number; vol?: number; slide?: number };
function hqShot(oscs: Osc[], dur: number, opts: {
  filter?: { type: BiquadFilterType; freq: number; Q?: number; freqEnd?: number };
  attack?: number;
  decay?: number;
  reverbSend?: number;
  vol?: number;
} = {}): void {
  const ac = ensure();
  const t0 = ac.currentTime;
  const attack = opts.attack ?? 0.005;
  const masterVol = (opts.vol ?? 1) * logGain(settings.sfx);
  const out = ac.createGain();
  out.gain.setValueAtTime(0, t0);
  out.gain.linearRampToValueAtTime(masterVol, t0 + attack);
  const decay = opts.decay ?? dur;
  out.gain.exponentialRampToValueAtTime(0.001, t0 + decay);
  let chainHead: AudioNode = out;
  if (opts.filter) {
    const f = ac.createBiquadFilter();
    f.type = opts.filter.type;
    f.frequency.setValueAtTime(opts.filter.freq, t0);
    if (opts.filter.freqEnd !== undefined) {
      f.frequency.exponentialRampToValueAtTime(Math.max(20, opts.filter.freqEnd), t0 + dur);
    }
    f.Q.value = opts.filter.Q ?? 1;
    f.connect(out);
    chainHead = f;
  }
  for (const o of oscs) {
    const osc = ac.createOscillator();
    osc.type = o.type;
    osc.frequency.value = o.freq;
    if (o.detune) osc.detune.value = o.detune;
    if (o.slide) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.freq + o.slide), t0 + dur);
    }
    const og = ac.createGain();
    og.gain.value = o.vol ?? 1;
    osc.connect(og).connect(chainHead);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }
  out.connect(MASTER!);
  if (opts.reverbSend && REVERB) {
    const rg = ac.createGain();
    rg.gain.value = opts.reverbSend;
    out.connect(rg).connect(REVERB);
  }
}

function shootPulse(): void {  // PULSE / TWIN — sharp pluck with sub-click
  if (!gate('w_pulse', 45)) return;
  const j = 1 + (Math.random() - 0.5) * 0.04;
  hqShot([
    { type: 'square',   freq: 900 * j, vol: 0.45, slide: -300 },
    { type: 'sine',     freq: 1800 * j, vol: 0.20, slide: -400 },
    { type: 'triangle', freq: 450 * j, vol: 0.18, slide: -150 },
  ], 0.08, { vol: 0.32, decay: 0.10, filter: { type: 'lowpass', freq: 4500, Q: 2 } });
  noiseShot(3500, 6, 0.025, 0.10);
}
function shootRapid(): void {  // RAPID — tight zappy click
  if (!gate('w_rapid', 26)) return;
  const j = 1 + (Math.random() - 0.5) * 0.06;
  hqShot([
    { type: 'square',   freq: 1450 * j, vol: 0.40, slide: -700 },
    { type: 'triangle', freq: 2900 * j, vol: 0.18, slide: -1400 },
  ], 0.04, { vol: 0.28, filter: { type: 'highpass', freq: 600 } });
  noiseShot(5500, 8, 0.015, 0.08);
}
function shootBlaster(): void {  // BLASTER — heavy sub-thump
  if (!gate('w_blaster', 55)) return;
  hqShot([
    { type: 'sine',     freq: 90,  vol: 0.55, slide: -50 },
    { type: 'sawtooth', freq: 180, vol: 0.45, slide: -120 },
    { type: 'square',   freq: 540, vol: 0.18, slide: -300 },
  ], 0.18, { vol: 0.38, filter: { type: 'lowpass', freq: 1400, Q: 4, freqEnd: 280 }, attack: 0.002 });
  noiseShot(900, 1.2, 0.06, 0.18);
}
function shootLaser(): void {  // LASER — stacked-harmonic descending glide + breath
  if (!gate('w_laser', 70)) return;
  hqShot([
    { type: 'sawtooth', freq: 2400, detune: -10, vol: 0.30, slide: -1900 },
    { type: 'sawtooth', freq: 2400, detune: +10, vol: 0.30, slide: -1900 },
    { type: 'square',   freq: 4800, vol: 0.10, slide: -3800 },
  ], 0.18, { vol: 0.30, decay: 0.20, filter: { type: 'lowpass', freq: 6000, Q: 2 }, reverbSend: 0.4 });
  noiseShot(4000, 4, 0.06, 0.08);
}
function shootPlasma(): void {  // PLASMA — fizzy detuned saw spit + bandpass burst
  if (!gate('w_plasma', 70)) return;
  hqShot([
    { type: 'sawtooth', freq: 660, detune: -12, vol: 0.30, slide: 240 },
    { type: 'sawtooth', freq: 660, detune: +12, vol: 0.30, slide: 240 },
    { type: 'triangle', freq: 1320, vol: 0.15, slide: 0 },
  ], 0.10, { vol: 0.28, filter: { type: 'bandpass', freq: 1200, Q: 3, freqEnd: 600 }, reverbSend: 0.5 });
  noiseShot(2200, 6, 0.10, 0.22);
  noiseShot(800, 2, 0.04, 0.10);
}
function shootHoming(): void {  // HOMING — rising harmonic chirp with vibrato feel
  if (!gate('w_homing', 85)) return;
  hqShot([
    { type: 'sine',     freq: 420, vol: 0.45, slide: 600 },
    { type: 'triangle', freq: 630, vol: 0.20, slide: 900 },
    { type: 'sine',     freq: 1260, vol: 0.10, slide: 1800 },
  ], 0.18, { vol: 0.30, attack: 0.01, reverbSend: 0.5 });
}
function shootBomb(): void {  // BOMB — triple-stacked sub launcher with crack + echo
  if (!gate('w_bomb', 110)) return;
  hqShot([
    { type: 'sine',     freq: 55,  vol: 0.55, slide: -25 },
    { type: 'sawtooth', freq: 110, vol: 0.40, slide: -55 },
    { type: 'square',   freq: 220, vol: 0.20, slide: -100 },
  ], 0.28, { vol: 0.45, filter: { type: 'lowpass', freq: 800, Q: 5, freqEnd: 200 }, reverbSend: 0.6 });
  noiseShot(380, 1.5, 0.18, 0.30);
  // Echo layer ~0.18s later for depth
  setTimeout(() => { hqShot([{ type: 'sine', freq: 70, vol: 0.4, slide: -20 }], 0.20, { vol: 0.20, reverbSend: 0.6 }); }, 180);
}
function shootRing(): void {  // RING — bell-attack chord stab
  if (!gate('w_ring', 90)) return;
  hqShot([
    { type: 'sine',     freq: 440, vol: 0.30 },
    { type: 'sine',     freq: 660, vol: 0.30 },
    { type: 'sine',     freq: 880, vol: 0.30 },
    { type: 'triangle', freq: 1320, vol: 0.20 },
    { type: 'square',   freq: 220, vol: 0.15 },
  ], 0.20, { vol: 0.32, attack: 0.002, decay: 0.30, reverbSend: 0.7 });
  noiseShot(2200, 6, 0.02, 0.10);
}
function shootChaos(): void {  // CHAOS — randomized pitch + noise burst
  if (!gate('w_chaos', 55)) return;
  const f = 400 + Math.random() * 1800;
  hqShot([
    { type: 'square',   freq: f, vol: 0.38, slide: -300 + Math.random() * 600 },
    { type: 'sawtooth', freq: f * 1.5, vol: 0.20, slide: -200 },
    { type: 'sine',     freq: f * 0.5, vol: 0.18 },
  ], 0.08, { vol: 0.28, filter: { type: 'bandpass', freq: 1200, Q: 4 } });
  noiseShot(f * 2, 4, 0.04, 0.12);
}
function shootDeathRay(): void {  // DEATH RAY / OMÉGA — full power-chord stack + sub + grit
  if (!gate('w_dray', 95)) return;
  hqShot([
    { type: 'sawtooth', freq: 110, detune: -8, vol: 0.40, slide: -20 },
    { type: 'sawtooth', freq: 110, detune: +8, vol: 0.40, slide: -20 },
    { type: 'sawtooth', freq: 165, vol: 0.30 },
    { type: 'sawtooth', freq: 220, vol: 0.30 },
    { type: 'sawtooth', freq: 330, vol: 0.25 },
    { type: 'square',   freq: 55,  vol: 0.30 },
  ], 0.30, { vol: 0.40, filter: { type: 'lowpass', freq: 3500, Q: 2 }, reverbSend: 0.5 });
  noiseShot(2400, 4, 0.18, 0.18);
  noiseShot(800, 2, 0.10, 0.14);
}
function shootZero(): void {  // ABSOLUTE ZERO — crystalline bell-glissando
  if (!gate('w_zero', 80)) return;
  hqShot([
    { type: 'sine',     freq: 2200, vol: 0.30, slide: -1200 },
    { type: 'triangle', freq: 1760, vol: 0.18 },
    { type: 'triangle', freq: 2640, vol: 0.18 },
    { type: 'sine',     freq: 4400, vol: 0.10, slide: -800 },
  ], 0.30, { vol: 0.26, attack: 0.008, reverbSend: 0.85 });
  noiseShot(6000, 6, 0.04, 0.08);
}

function shootByWeapon(name: string): void {
  const n = (name || '').toUpperCase();
  // Tier 6+ cosmic / mythic / transcendent / ultimate names get unique synthesizers
  if (n.indexOf('SINGUL') >= 0 || n.indexOf('WORMHOLE') >= 0 || n.indexOf('PARADOX') >= 0) return shootZero();
  if (n.indexOf('OMNIPOTENT') >= 0 || n.indexOf('ULTIMA') >= 0 || n.indexOf('PARADIGM') >= 0
      || n.indexOf('OMEGA') >= 0 || n.indexOf('OMÉGA') >= 0 || n.indexOf('TITAN') >= 0
      || n.indexOf('ZÉNITH') >= 0 || n.indexOf('APEX') >= 0 || n.indexOf('PRIMUS') >= 0) return shootDeathRay();
  if (n.indexOf('FRACTAL') >= 0 || n.indexOf('PRISME') >= 0 || n.indexOf('CHIMÈRE') >= 0) return shootChaos();
  if (n.indexOf('SUPERNOVA') >= 0 || n.indexOf('NOVA') >= 0 || n.indexOf('NÉBULEUSE') >= 0
      || n.indexOf('TYPHON') >= 0 || n.indexOf('TEMPÊTE') >= 0 || n.indexOf('MAELSTRÖM') >= 0) return shootRing();
  if (n.indexOf('ANTIMATIÈRE') >= 0 || n.indexOf('VOID') >= 0 || n.indexOf('OBSIDIENNE') >= 0
      || n.indexOf('ÉCLIPSE') >= 0) return shootBomb();
  if (n.indexOf('PHŒNIX') >= 0 || n.indexOf('AURORE') >= 0 || n.indexOf('GENÈSE') >= 0
      || n.indexOf('ÉTOILE') >= 0 || n.indexOf('COSMOS') >= 0 || n.indexOf('KAÏROS') >= 0
      || n.indexOf('MÉRIDIEN') >= 0 || n.indexOf('ARCHE') >= 0) return shootHoming();
  // Original tier matches (English + French)
  if (n.indexOf('ZERO') >= 0)               return shootZero();
  if (n.indexOf('DEATH') >= 0 || n.indexOf('DOOM') >= 0 || n.indexOf('RAYON') >= 0
      || n.indexOf('INFERNO') >= 0)         return shootDeathRay();
  if (n.indexOf('CHAOS') >= 0)              return shootChaos();
  if (n.indexOf('RING') >= 0 || n.indexOf('ANNEAU') >= 0) return shootRing();
  if (n.indexOf('BOMB') >= 0 || n.indexOf('BOMBE') >= 0 || n.indexOf('CLUSTER') >= 0
      || n.indexOf('GRAPPE') >= 0 || n.indexOf('ONDE') >= 0) return shootBomb();
  if (n.indexOf('HOMING') >= 0 || n.indexOf('GUIDÉ') >= 0 || n.indexOf('PULSAR') >= 0
      || n.indexOf('SPIRALE') >= 0 || n.indexOf('ARC') >= 0) return shootHoming();
  if (n.indexOf('PLASMA') >= 0 || n.indexOf('FLUX') >= 0)  return shootPlasma();
  if (n.indexOf('LASER') >= 0)              return shootLaser();
  if (n.indexOf('BLASTER') >= 0 || n.indexOf('BLASTEUR') >= 0) return shootBlaster();
  if (n.indexOf('RAPID') >= 0 || n.indexOf('RAPIDE') >= 0 || n.indexOf('RAFALE') >= 0) return shootRapid();
  return shootPulse();
}

function beacon(): void {
  const ac = ensure();
  const t0 = ac.currentTime;
  // NASA-style mission-comms beep: a clear 1kHz tone with a longer reverb tail.
  // Two ping pattern: high "beep" 1000Hz then lower "boop" 600Hz, ~0.30s each.
  const ping = (freq: number, t: number, dur: number, vol: number): void => {
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = 'sine';
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol * logGain(settings.sfx), t + 0.012);
    g.gain.linearRampToValueAtTime(vol * logGain(settings.sfx), t + dur - 0.06);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g);
    // Heavy reverb send — gives that "transmitting from the void" feel
    g.connect(MASTER!);
    if (REVERB) {
      const rg = ac.createGain();
      rg.gain.value = 1.4;
      g.connect(rg).connect(REVERB);
    }
    o.start(t); o.stop(t + dur + 0.05);
  };
  ping(1000, t0,         0.32, 0.45);
  ping(600,  t0 + 0.36,  0.36, 0.40);
}

function bossRoar(): void {
  const ac = ensure();
  const t0 = ac.currentTime;
  // Sub-rumble (sawtooth descending 90→25Hz over 1.5s)
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(90, t0);
  o.frequency.exponentialRampToValueAtTime(25, t0 + 1.5);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(0.45 * logGain(settings.sfx), t0 + 0.05);
  g.gain.linearRampToValueAtTime(0.40 * logGain(settings.sfx), t0 + 1.0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + 1.6);
  o.connect(g).connect(MASTER!);
  o.start(t0); o.stop(t0 + 1.7);
  // Growl — mid-band noise sweep
  const n = ac.createBufferSource();
  n.buffer = noiseBuffer(ac, 1.6);
  const nf = ac.createBiquadFilter();
  nf.type = 'bandpass';
  nf.frequency.setValueAtTime(800, t0);
  nf.frequency.exponentialRampToValueAtTime(180, t0 + 1.4);
  nf.Q.value = 3;
  const ng = ac.createGain();
  ng.gain.setValueAtTime(0.18 * logGain(settings.sfx), t0);
  ng.gain.exponentialRampToValueAtTime(0.001, t0 + 1.5);
  n.connect(nf).connect(ng).connect(MASTER!);
  n.start(t0); n.stop(t0 + 1.6);
}

// Per-pickup-kind SFX with distinct character.
function pickupHull(): void {
  // Warm bell — C5 + E5 stack
  tone(523, 0.10, 'sine', 0.18, 0);
  tone(659, 0.12, 'triangle', 0.14, 0);
}
function pickupShield(): void {
  // Sparkly chime — quick rising tones
  tone(880, 0.06, 'sine', 0.18, 200);
  setTimeout(() => tone(1320, 0.08, 'sine', 0.16, 100), 35);
  setTimeout(() => tone(1760, 0.10, 'triangle', 0.12, 0), 75);
}
function pickupSpecial(): void {
  // Ascending arpeggio C-E-G-C
  const ts = [0, 50, 100, 150];
  const hz = [523, 659, 784, 1047];
  for (let i = 0; i < 4; i++) setTimeout(() => tone(hz[i], 0.10, 'square', 0.16, 0), ts[i]);
}
function pickupLife(): void {
  // High triad fanfare — major chord stab
  chord([784, 988, 1175], 0.20, 'triangle', 0.18);
  setTimeout(() => chord([1175, 1480, 1760], 0.18, 'triangle', 0.15), 100);
}

export const SFX = {
  shoot(): void { if (gate('shoot', 50)) tone(900, 0.05, 'square', 0.12, -300); },
  shootByWeapon,
  // Layered hit — short noise transient on top of the original square thud for impact
  hit(): void {
    if (gate('hit', 35)) {
      tone(180, 0.08, 'square', 0.18, -60);
      noiseShot(2200, 4, 0.04, 0.10);
    }
  },
  boom(): void { if (gate('boom', 65)) tone(80, 0.3, 'sawtooth', 0.3, -40); },
  // Brighter rising upgrade phrase
  upgrade(): void {
    tone(660, 0.08, 'triangle', 0.22, 200);
    setTimeout(() => tone(990, 0.10, 'triangle', 0.20), 70);
    setTimeout(() => tone(1320, 0.12, 'sine', 0.18, 0), 140);
    setTimeout(() => chord([1320, 1760], 0.18, 'triangle', 0.14), 200);
  },
  special(): void { tone(440, 0.15, 'square', 0.25, -100); },
  // Death = falling-pitch synth crash with reverb-style noise tail
  death(): void {
    const ac = ensure();
    const t0 = ac.currentTime;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(440, t0);
    o.frequency.exponentialRampToValueAtTime(80, t0 + 0.7);
    g.gain.setValueAtTime(0.45 * logGain(settings.sfx), t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.8);
    o.connect(g);
    g.connect(MASTER!);
    if (REVERB) g.connect(REVERB);
    o.start(t0); o.stop(t0 + 0.85);
    // Noise crash tail
    noiseShot(1200, 2, 0.6, 0.18);
  },
  pickup(): void { if (gate('pickup', 30)) tone(800, 0.06, 'sine', 0.2, 200); },
  pickupHull,
  pickupShield,
  pickupSpecial,
  pickupLife,
  bossRoar,
  beacon,
  menuHover(): void { if (gate('hover', 30)) tone(1400, 0.04, 'sine', 0.10, 200); },
  menuClick(): void { if (gate('click', 30)) { tone(660, 0.04, 'square', 0.18, 220); tone(1320, 0.06, 'triangle', 0.12, 0); } },
};
